import { Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { TranslocoPipe } from '@jsverse/transloco';

const I18N = 'shared.dataSource';

/** `bar` = rodapé do card (Nuvem, DataStories); `block` = bloco de notas sob o gráfico (DataCuidados). */
export type DataSourceVariant = 'bar' | 'block';

/**
 * Procedência dos dados exibidos: fonte, notas e data de atualização.
 *
 * As telas divergem só na moldura — a barra de rodapé do card e o bloco de notas do
 * DataCuidados —, então o conteúdo e o tratamento das notas ficam aqui.
 */
@Component({
  selector: 'app-data-source',
  standalone: true,
  imports: [TranslocoPipe],
  host: { class: 'block' },
  template: `
    @if (variant() === 'bar') {
      <footer
        class="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-muted/30 px-6 py-3 text-xs text-muted-foreground"
      >
        <p class="flex items-center gap-1.5">
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="h-3 w-3 shrink-0"
          >
            <ellipse cx="12" cy="5" rx="9" ry="3" />
            <path d="M3 5V19A9 3 0 0 0 21 19V5" />
            <path d="M3 12A9 3 0 0 0 21 12" />
          </svg>
          {{ i18n + '.source' | transloco }}: {{ source() }}
        </p>
        @if (updatedAt()) {
          <p class="tabular-nums">{{ i18n + '.lastUpdate' | transloco }}: {{ updatedAt() }}</p>
        }
      </footer>
      @if (notes()) {
        <p
          class="border-t border-border bg-muted/30 px-6 pb-3 text-xs leading-relaxed text-muted-foreground"
          [innerHTML]="notesAsHtml()"
        ></p>
      }
    } @else {
      <small class="mt-4 block border-t border-border pt-4 text-xs leading-relaxed text-gray-600">
        <span class="block">
          <span class="font-semibold">{{ i18n + '.source' | transloco }}:</span> {{ source() }}
        </span>
        @if (notes()) {
          <span class="mt-1 block whitespace-pre-line" [innerHTML]="notesAsHtml()"></span>
        }
        @if (updatedAt()) {
          <span class="mt-1 block">{{ i18n + '.lastUpdate' | transloco }}: {{ updatedAt() }}</span>
        }
      </small>
    }
  `,
})
export class DataSourceComponent {
  private readonly sanitizer = inject(DomSanitizer);

  readonly source = input('');
  /** Notas/limitações; quebras de linha viram `<br>` e "Nota(s):" fica em negrito. */
  readonly notes = input('');
  readonly updatedAt = input('');
  readonly variant = input<DataSourceVariant>('bar');

  protected readonly i18n = I18N;

  /** Escapa o texto antes de reintroduzir só a marcação que queremos — nunca confia na API. */
  protected readonly notesAsHtml = computed<SafeHtml>(() => {
    const text = this.notes();
    if (!text) return this.sanitizer.bypassSecurityTrustHtml('');
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    const withBold = escaped.replace(/Nota\(s\):/g, '<strong>Nota(s):</strong>');
    return this.sanitizer.bypassSecurityTrustHtml(withBold.replace(/\n/g, '<br>'));
  });
}
